import { Box, Button, Grid2, Paper, Typography } from "@mui/material";
import styles from "../nav-guide.module.scss";
import { NavLink, useNavigate } from "react-router-dom";
import { CheckCircle } from "@mui/icons-material";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";

export default function NavGuide() {
  const navigate = useNavigate();

  const isExternalUrl = (url: string) => {
    return url.startsWith('http://') || url.startsWith('https://');
  };

  const appList = [
    {
      key: "art-agent-lab",
      to: "/agent-builder/dashboard",
      imgSrc: "/assets/images/agent-lab.svg",
      alt: "Art Agent Lab",
      description:
        "An engine for seamless real-time interactions, optimized for instant & logical message routing.",
      features: [
        "AI powered orchestration for real-time interactions",
        "Customizable agent workflows for your business needs",
        "Integration with external APIs and services for enhanced functionalityl",
      ],
      buttonText: "Explore Agent Lab",
    },
    {
      key: "art-live",
      to: "/live/dashboard",
      imgSrc: "/assets/images/live-connect.svg",
      alt: "Art Live Connect",
      description:
        "An engine for seamless real-time interactions, optimized for instant & logical message routing.",
      features: [
        "Persistent, bidirectional WebSocket connections",
        "Logical message orchestration with AI integration",
        "Interceptors for smart filtering and transformation",
      ],
      buttonText: "Explore Live",
    },
    {
      key: "art-docs",
      to: "https://docs.arealtimetech.com/",
      imgSrc: "/assets/images/docs_logo.png",
      alt: "ART Docs",
      description:
        "Easily access comprehensive documentation to help you integrate, customize, and manage your application with confidence.",
      features: [
        "Step-by-step integration guides",
        "Setup and configuration instructions",
        "Troubleshooting and FAQs",
      ],
      buttonText: "Explore Docs",
    },
  ];

  const handleCardClick = (to: string) => {
    if (isExternalUrl(to)) {
      window.location.href = to;
    } else {
      navigate(to);
    }
  };

  return (
    <div className={styles.container}>
      <Box className={styles.container_wrapper}>
        <Grid2
          container
          spacing={5}
          alignItems="center"
          justifyContent="center"
          direction={{ xs: "column", md: "row" }}
        >
          <Grid2 size={{ xs: 12, md: 7 }} sx={{ order: { xs: 2, md: 1 } }}>
            <Typography
              variant="h5"
              textAlign="start"
              sx={{
                fontSize: { xs: "24px", sm: "30px", md: "36px", lg: "45px" },
              }}
            >
              Explore the ART Suite
            </Typography>
            <Typography
              variant="body1"
              color="#171D1E"
              textAlign="start"
              sx={{ py: 2, fontSize: { xs: "13px", sm: "14px", md: "15px" } }}
            >
              The ART ecosystem is an all-in-one platform designed to transform
              how businesses operate using real-time systems and intelligent
              tools. From managing live data and building AI agents to accessing
              plug-and-play utilities, everything comes together in one
              streamlined environment. Built for speed, intelligence, and
              control, the ART Suite simplifies the path from idea to execution.
            </Typography>
          </Grid2>
          <Grid2
            size={{ xs: 12, md: 5 }}
            textAlign="center"
            sx={{ order: { xs: 1, md: 2 } }}
          >
            <img
              src="/assets/images/all_set.svg"
              alt="image"
              width={300}
            />
          </Grid2>
        </Grid2>
      </Box>

      <div style={{ 'padding': "16px" }}>
        <Grid2
          container
          spacing={{ xs: 3, sm: 6, md: 6, lg: 8 }}
          pt={{ xs: 3, sm: 2, md: 1, lg: 6 }}
        >
          {appList.map(
            ({ key, to, imgSrc, alt, description, features, buttonText }) => {
              return (
                <Grid2 size={{ xs: 12, sm: 6, md: 4.5, lg: 4.5 }} key={key} sx={{ position: 'relative', zIndex: 999 }}>
                  <Paper className={styles.card} sx={{ px: 3, position: 'relative', zIndex: 1 }}>
                    <div className={styles.product_block}>
                      <Box>
                        <div className={styles.product_logo}>
                          <img
                            src={imgSrc}
                            alt={alt}
                            className="art_app_logo_full img_fit_contain"
                          />
                        </div>
                        <p>{description}</p>
                        <ul className={styles.feature_list}>
                          {features?.map((item, idx) => (
                            <li key={idx}>
                              <CheckCircle
                                fontSize="large"
                                sx={{ pr: 2 }}
                                className={styles.check_icon}
                              />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </Box>
                      <Box
                        sx={{ mt: 2 }}
                        className={styles.card_button_container}
                      >
                        <Box mt={2}>
                          {isExternalUrl(to) ? (
                            <Button
                              variant="contained"
                              color="primary"
                              href={to}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles.card_button}
                              onClick={(e) => e.stopPropagation()}
                              data-qa={key}
                            >
                              {buttonText}
                              <span className={styles.arrow_icon_up}>
                                {" "}
                                <ArrowOutwardIcon />
                              </span>
                              <span className={styles.arrow_icon_down}>
                                {" "}
                                <ArrowOutwardIcon />
                              </span>
                            </Button>
                          ) : (
                            <Button
                              variant="contained"
                              color="primary"
                              component={NavLink}
                              to={to}
                              className={styles.card_button}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCardClick(to);
                              }}
                              data-qa={key}
                            >
                              {buttonText}
                              <span className={styles.arrow_icon_up}>
                                {" "}
                                <ArrowOutwardIcon />
                              </span>
                              <span className={styles.arrow_icon_down}>
                                {" "}
                                <ArrowOutwardIcon />
                              </span>
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </div>
                  </Paper>
                </Grid2>
              );
            }
          )}
        </Grid2>
      </div>
    </div>
  );
}
